import { epochDayOf } from '../../../core/services/plain-date';

import type { GanttItem, Row, Span } from '../types';

/**
 * An item's own span, inclusive of both end days. An item carrying only one of the two dates
 * gets a one-day span anchored on the date it has — the fade-gradient treatment on the missing
 * end is a rendering concern, not a layout one.
 */
export const spanOf = (item: GanttItem): Span | undefined => {
  const start = item.startDate ? epochDayOf(item.startDate) : undefined;
  const due = item.dueDate ? epochDayOf(item.dueDate) : undefined;

  if (start === undefined && due === undefined) return undefined;
  if (start === undefined) return { startDay: due, endDay: due };
  if (due === undefined) return { startDay: start, endDay: start };

  // Never trust the stored pair to be ordered: an inverted pair must not become a negative width.
  return { startDay: Math.min(start, due), endDay: Math.max(start, due) };
};

const unionSpans = (a: Span | undefined, b: Span | undefined): Span | undefined => {
  if (!a) return b;
  if (!b) return a;

  return { startDay: Math.min(a.startDay, b.startDay), endDay: Math.max(a.endDay, b.endDay) };
};

/**
 * Post-order half of `buildRows`: writes each item's resolved span into `into` and returns the
 * union of that item's OWN dates with every descendant's own dates.
 *
 * The distinction matters one level up. A group's span is the union of its descendants' own
 * dates — not of their resolved spans — so a dated intermediate group still contributes its own
 * dates to its parent even though its own span was overridden by its children's.
 *
 * One walk per tree, so the whole model is O(N); the previous shape re-walked each group's
 * subtree once per group row, which is O(N x depth).
 */
const resolveSpans = (
  item: GanttItem,
  into: Map<GanttItem, Span | undefined>,
): Span | undefined => {
  const own = spanOf(item);

  let descendants: Span | undefined;
  item.children?.forEach((child) => {
    descendants = unionSpans(descendants, resolveSpans(child, into));
  });

  // A group falls back to its OWN dates only when nothing below it is dated. The current
  // provider discards a group's own dates whenever `subItems` is present at all — and the
  // project route always passes an array, empty or not — so a dated stage with no steps renders
  // no bar. A leaf has no descendants, so this is just its own span.
  into.set(item, descendants ?? own);

  return unionSpans(own, descendants);
};

export type RowModel = {
  /**
   * The item tree flattened depth-first into the ONE ordered array every layer consumes: the
   * sticky row headers, the bars, the off-screen indicators and (later) the dependency arrows.
   * Collapse is expressed by absence — a hidden row is not in the array — so no consumer can
   * disagree with another about which rows exist or what order they are in.
   */
  rows: Row[];
  /**
   * The row that is actually rendered for `id` — itself if visible, otherwise the collapsed
   * ancestor standing in for it.
   *
   * NO PRODUCTION CALLER TODAY. `row-list.tsx` draws an off-screen indicator straight from the
   * visible `row.span` and never resolves a hidden id, so an earlier claim here that this runs
   * once per visible row per frame was simply untrue. It is kept as a Phase 2 seam: a dependency
   * arrow into a collapsed subtree has to terminate on a row that exists, and that is the
   * question this answers.
   *
   * Free to keep, which is why keeping it is defensible: the map is filled during the walk
   * `buildRows` already performs, so it costs one `Map.set` per item and no extra traversal, and
   * a caller pays O(1). The shape it replaces — `rows.find(...)` plus a descendant walk — would
   * be O(n x subtree) per call.
   *
   * Unambiguous, not merely arbitrary: only the OUTERMOST collapsed ancestor of a hidden item is
   * itself present in `rows`, so exactly one row can ever stand in for a given id.
   */
  ancestorRowOf: (id: string) => Row | undefined;
};

/**
 * Flattens `items` into the visible row array and, in the same walk, the hidden-id -> standing-in
 * row map. Both halves need the same traversal, and building them apart is how they drift.
 *
 * Spans are derived here rather than by a second pass a caller has to remember: skipping that
 * pass used to degrade silently to a today-only window with no bars at all. Because the span
 * walk covers the WHOLE tree, a collapsed group still spans the children it is hiding.
 */
export const buildRows = (items: GanttItem[], collapsed: ReadonlySet<string>): RowModel => {
  const spans = new Map<GanttItem, Span | undefined>();
  items.forEach((item) => resolveSpans(item, spans));

  const rows: Row[] = [];
  const standIn = new Map<string, Row>();

  // `hiddenUnder` is the visible collapsed row this item is buried under, or undefined when the
  // item is visible itself. It is set once, at the outermost collapse, and carried down — a
  // nested collapse below it changes nothing, because that row is not rendered either.
  const visit = (
    item: GanttItem,
    depth: number,
    parentId: string | undefined,
    hiddenUnder: Row | undefined,
  ): void => {
    if (hiddenUnder) {
      standIn.set(item.id, hiddenUnder);
      item.children?.forEach((child) => visit(child, depth + 1, item.id, hiddenUnder));

      return;
    }

    const isCollapsed = collapsed.has(item.id);

    const row: Row = {
      id: item.id,
      item,
      parentId,
      depth,
      index: rows.length,
      // An empty `children` array still means "a group with nothing in it", not a leaf. The old
      // provider's `subItems ? … : …` made the same distinction and then discarded the group's
      // own dates because of it; the distinction is right, the consequence was not.
      isGroup: Array.isArray(item.children),
      isCollapsed,
      span: spans.get(item),
    };

    rows.push(row);
    standIn.set(item.id, row);

    item.children?.forEach((child) =>
      visit(child, depth + 1, item.id, isCollapsed ? row : undefined),
    );
  };

  items.forEach((item) => visit(item, 0, undefined, undefined));

  return { rows, ancestorRowOf: (id) => standIn.get(id) };
};
